/**
 * One-off or subscribe (blocks/purchase-options.liquid), on the product page and in the quick add
 * picker.
 *
 *   - Posts nothing itself. The pills are the `selling_plan` field, radios joined to the product
 *     form by its id (the block's `form` attribute), so what is checked is what is posted with or
 *     without this script. It used to mirror the pill into a hidden input it created in the form,
 *     which meant that without JavaScript the plan shown was never sent. In the quick add picker,
 *     assets/quick-add.js gives the moved form its own id and repoints the pills.
 *   - Leaves the Add button's words alone (Matt, 2026-10-15). It used to relabel it "Subscribe"
 *     while a plan was chosen; the pill and the terms above the button state the commitment.
 *   - Names the chosen plan to assistive technology on the product form's Add button
 *     (aria-describedby, "Repeat delivery: Every 4 weeks"), since the button's own words no longer
 *     say so; nothing for a one-off.
 *   - Fills in the saving for the current quantity when the chosen plan has a single-phase price
 *     below the one-off price. Which of the terms and the One-off note shows is CSS, from the
 *     checked pill, so it holds without this script.
 *   - Tells the upsell line what is chosen (`purchase-options:change` on the section), and takes
 *     `purchase-options:select-default` from it ("Deliver every 4 weeks").
 */
import { formatMoney } from '@theme/money-formatting';

const QUANTITY_EVENT = 'quantity-selector:update';
export const CHANGE_EVENT = 'purchase-options:change';
export const SELECT_DEFAULT_EVENT = 'purchase-options:select-default';

let summaries = 0;

class PurchaseOptions extends HTMLElement {
  /** @type {HTMLElement | null} */
  #summary = null;

  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.addEventListener('change', this.#sync);
    this.section?.addEventListener(QUANTITY_EVENT, this.#onQuantity);
    this.section?.addEventListener(SELECT_DEFAULT_EVENT, this.#selectDefault);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#sync);
    this.section?.removeEventListener(QUANTITY_EVENT, this.#onQuantity);
    this.section?.removeEventListener(SELECT_DEFAULT_EVENT, this.#selectDefault);
  }

  #quantity() {
    for (const input of this.section?.querySelectorAll('quantity-selector-component input[name="quantity"]') ?? []) {
      if (!input.closest('product-card, cart-items-component')) return parseInt(/** @type {HTMLInputElement} */ (input).value, 10) || 1;
    }
    return 1;
  }

  #checked() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked'));
  }

  #sync = () => {
    const chosen = this.#checked();
    const plan = chosen?.value ?? '';

    this.#showSaving();
    this.#describe(plan === '' ? '' : chosen?.closest('label')?.textContent?.trim() ?? '');

    this.section?.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { bubbles: false, detail: { plan, saving: Number(chosen?.dataset.saving) || 0 } })
    );
  };

  /** The product's own add-to-cart form, never a card's. */
  #form() {
    for (const form of this.section?.querySelectorAll('form[data-type="add-to-cart-form"]') ?? []) {
      if (!form.closest('product-card')) return /** @type {HTMLFormElement} */ (form);
    }
    return null;
  }

  /**
   * A visually hidden line, made here so its id is unique even when the quick add picker holds a
   * second copy of this block, that the Add button points to while a plan is chosen.
   * @param {string} plan the chosen plan's name, or '' for a one-off
   */
  #describe(plan) {
    const button = this.#form()?.querySelector('button[name="add"]');
    if (!button) return;
    if (!this.#summary) {
      this.#summary = document.createElement('span');
      this.#summary.id = `purchase-summary-${++summaries}`;
      this.#summary.className = 'visually-hidden';
      this.append(this.#summary);
    }
    this.#summary.textContent = plan ? (this.dataset.summaryTemplate ?? '').replace('[plan]', plan) : '';
    const ids = (button.getAttribute('aria-describedby') ?? '').split(/\s+/).filter((id) => id && id !== this.#summary.id);
    if (plan) ids.push(this.#summary.id);
    if (ids.length) button.setAttribute('aria-describedby', ids.join(' '));
    else button.removeAttribute('aria-describedby');
  }

  #onQuantity = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card, cart-items-component')) return;
    this.#showSaving();
  };

  #showSaving() {
    const out = this.querySelector('.purchase-options__saving');
    if (!out) return;
    const saving = Number(this.#checked()?.dataset.saving) || 0;
    if (!saving) {
      out.textContent = '';
      return;
    }
    const amount = formatMoney(saving * this.#quantity(), this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? 'GBP');
    out.textContent = (this.dataset.savingTemplate ?? '').replace('[amount]', amount);
  }

  #selectDefault = () => {
    const radio = this.querySelector(`input[type="radio"][value="${CSS.escape(this.dataset.defaultPlan ?? '')}"]`);
    if (!(radio instanceof HTMLInputElement) || radio.checked) return;
    radio.checked = true;
    this.#sync();
    radio.focus({ preventScroll: true });
  };
}

if (!customElements.get('purchase-options')) customElements.define('purchase-options', PurchaseOptions);
